/**
 * "Voltage" — original composition for "Overdrive". 150 BPM, E minor,
 * 32-beat loop: relentless kick, 16th hats, driving octave bass, sawtooth
 * stab lead. Built for the speed-portal gauntlet.
 */
import type { Track, NoteEvent } from '../../audio/track';

const E1 = 28,
  G1 = 31,
  A1 = 33,
  B1 = 35;
const G3 = 55,
  A3 = 57,
  B3 = 59,
  E4 = 64,
  D4 = 62,
  FS4 = 66,
  G4 = 67;

function beats(n: number, step: number, offset = 0): number[] {
  const out: number[] = [];
  for (let b = offset; b < n; b += step) out.push(b);
  return out;
}

function octaveBass(startBeat: number, root: number): NoteEvent[] {
  const out: NoteEvent[] = [];
  for (let i = 0; i < 16; i++) {
    out.push({
      beat: startBeat + i * 0.5,
      midi: root + (i % 2 === 1 ? 12 : 0),
      len: 0.4,
      vel: i % 4 === 0 ? 1 : 0.7,
    });
  }
  return out;
}

function stabs(startBeat: number, notes: number[]): NoteEvent[] {
  const out: NoteEvent[] = [];
  const pattern = [0, 0.75, 1.5, 2, 2.75, 3.5, 4, 4.75, 5.5, 6, 6.5, 7, 7.5];
  pattern.forEach((b, i) => {
    out.push({ beat: startBeat + b, midi: notes[i % notes.length] ?? 64, len: 0.3, vel: 0.6 });
  });
  return out;
}

const CHORDS: Array<{ root: number; stab: number[] }> = [
  { root: E1, stab: [E4, B3, G3, E4] },
  { root: G1, stab: [G4, D4, B3, G4] },
  { root: A1, stab: [A3 + 12, E4, A3, FS4] },
  { root: B1, stab: [B3 + 12, FS4, D4, B3] },
];

const LOOP_BEATS = 32;
const bass: NoteEvent[] = [];
const lead: NoteEvent[] = [];
const pad: NoteEvent[] = [];
CHORDS.forEach((ch, i) => {
  const start = i * 8;
  bass.push(...octaveBass(start, ch.root));
  lead.push(...stabs(start, ch.stab));
  pad.push({ beat: start, midi: ch.root + 24, len: 7.5, vel: 0.22 });
  pad.push({ beat: start, midi: ch.root + 31, len: 7.5, vel: 0.18 });
});

export const VOLTAGE: Track = {
  id: 'voltage',
  title: 'Voltage',
  bpm: 150,
  loopBeats: LOOP_BEATS,
  kick: beats(LOOP_BEATS, 1),
  hat: beats(LOOP_BEATS, 0.25, 0.25).filter((b) => b % 1 !== 0),
  snare: beats(LOOP_BEATS, 2, 1),
  bass,
  lead,
  pad,
};
